import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: string;
  jsonLd?: Record<string, unknown>;
  breadcrumbs?: BreadcrumbItem[];
  keywords?: string;
}

const BASE_URL = "https://ultraservicos.com.br";

const SEO = ({ title, description, canonical, type = "website", jsonLd, breadcrumbs, keywords }: SEOProps) => {
  const location = useLocation();
  const url = canonical || `${BASE_URL}${location.pathname}`;
  const fullTitle = title;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("og:title", fullTitle, true);
    setMeta("og:description", description, true);
    setMeta("og:url", url, true);
    setMeta("og:type", type, true);
    setMeta("og:image", `${BASE_URL}/og-image.jpg`, true);
    setMeta("og:image:type", "image/jpeg", true);
    setMeta("og:image:width", "1456", true);
    setMeta("og:image:height", "816", true);
    setMeta("og:locale", "pt_BR", true);
    setMeta("og:site_name", "Dedetizadora USA - Ultra Serviços Ambientais", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", `${BASE_URL}/og-image.jpg`);
    if (keywords) {
      setMeta("keywords", keywords);
    }
    const isRioPage = location.pathname.startsWith("/rio-de-janeiro");
    setMeta("geo.region", isRioPage ? "BR-RJ" : "BR-DF");
    setMeta("geo.placename", isRioPage ? "Rio de Janeiro" : "Brasília");

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = url;

    // JSON-LD per page
    if (jsonLd) {
      const existingScript = document.getElementById("page-jsonld");
      if (existingScript) existingScript.remove();
      const script = document.createElement("script");
      script.id = "page-jsonld";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    // Breadcrumbs JSON-LD
    if (breadcrumbs && breadcrumbs.length > 0) {
      const existingBc = document.getElementById("breadcrumb-jsonld");
      if (existingBc) existingBc.remove();
      const bcScript = document.createElement("script");
      bcScript.id = "breadcrumb-jsonld";
      bcScript.type = "application/ld+json";
      bcScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((bc, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": bc.name,
          "item": bc.url,
        })),
      });
      document.head.appendChild(bcScript);
    }

    return () => {
      const script = document.getElementById("page-jsonld");
      if (script) script.remove();
      const bcScript = document.getElementById("breadcrumb-jsonld");
      if (bcScript) bcScript.remove();
    };
  }, [fullTitle, description, url, type, jsonLd, breadcrumbs, keywords]);

  return null;
};

export default SEO;
